import { useEffect, useState } from "react";
import { Download, Share, X } from "lucide-react";

function isRunningStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIOSSafari() {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
  return isIOS && isSafari;
}

/** Encapsulates all install-prompt platform detection + dismiss/install actions. */
function useInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === "undefined") return false;
    return sessionStorage.getItem("install-hint-dismissed") === "1";
  });
  const [showIOSHint] = useState(() => !isRunningStandalone() && isIOSSafari());

  useEffect(() => {
    if (isRunningStandalone()) return undefined;
    const onBIP = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    window.addEventListener("beforeinstallprompt", onBIP);
    return () => window.removeEventListener("beforeinstallprompt", onBIP);
  }, []);

  const dismiss = () => {
    sessionStorage.setItem("install-hint-dismissed", "1");
    setDismissed(true);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const choice = await deferred.userChoice;
    if (choice?.outcome === "accepted") dismiss();
    setDeferred(null);
  };

  return { deferred, dismissed, showIOSHint, dismiss, install };
}

function AndroidInstallPrompt({ onInstall }) {
  return (
    <>
      <Download size={18} style={{ color: "var(--green)", flexShrink: 0 }} />
      <div style={{ flex: 1, fontSize: 13, lineHeight: 1.4 }}>
        <div style={{ fontWeight: 600 }}>Install RollOSS</div>
        <div style={{ color: "var(--text-dim)", fontSize: 12 }}>
          Add to your home screen for fast access.
        </div>
      </div>
      <button
        onClick={onInstall}
        data-testid="install-app-btn"
        className="btn btn-primary"
        style={{ padding: "6px 12px", fontSize: 12 }}
      >
        Install
      </button>
    </>
  );
}

function IOSInstallHint() {
  return (
    <>
      <Share size={18} style={{ color: "var(--green)", flexShrink: 0 }} />
      <div style={{ flex: 1, fontSize: 13, lineHeight: 1.4 }}>
        <div style={{ fontWeight: 600 }}>Install on iPhone</div>
        <div style={{ color: "var(--text-dim)", fontSize: 12 }}>
          Tap <Share size={11} style={{ display: "inline", verticalAlign: "middle" }} /> Share →
          <strong style={{ color: "var(--text)" }}> Add to Home Screen</strong>
        </div>
      </div>
    </>
  );
}

/**
 * InstallAppHint
 * Shows a small "Install RollOSS" CTA on Android/Desktop Chrome (uses beforeinstallprompt),
 * or an iOS-specific Add-to-Home-Screen hint on Safari iOS.
 * Hides itself if the app is already installed (display-mode: standalone) or dismissed this session.
 */
export default function InstallAppHint() {
  const { deferred, dismissed, showIOSHint, dismiss, install } = useInstallPrompt();

  if (dismissed) return null;
  if (!deferred && !showIOSHint) return null;

  return (
    <div
      data-testid="install-app-hint"
      style={{
        marginTop: 16,
        padding: "12px 14px",
        border: "1px solid var(--border)",
        borderRadius: 12,
        background: "rgba(61,220,126,0.06)",
        display: "flex",
        alignItems: "center",
        gap: 10,
      }}
    >
      {deferred ? <AndroidInstallPrompt onInstall={install} /> : <IOSInstallHint />}
      <button
        onClick={dismiss}
        data-testid="install-hint-dismiss"
        aria-label="Dismiss"
        style={{ background: "transparent", border: 0, color: "var(--text-dim)", cursor: "pointer", padding: 4 }}
      >
        <X size={16} />
      </button>
    </div>
  );
}
